/**
 * Analytics loader — loads tracking scripts only after cookie consent.
 *
 * Supported analytics platforms:
 *   - Google Analytics 4 (REACT_APP_GOOGLE_ANALYTICS_ID)
 *   - Plausible          (REACT_APP_PLAUSIBLE_DOMAINS)
 *   - PostHog            (REACT_APP_POSTHOG_API_KEY + REACT_APP_POSTHOG_HOST)
 *
 * Each script is injected once — subsequent calls to loadAnalytics() are no-ops.
 */

const SCRIPT_IDS = {
  GA: 'analytics-ga4',
  PLAUSIBLE: 'analytics-plausible',
  POSTHOG: 'analytics-posthog',
};

const injectScript = (id, src, attributes = {}) => {
  if (document.getElementById(id)) return; // already loaded
  const script = document.createElement('script');
  script.id = id;
  script.src = src;
  script.async = true;
  Object.entries(attributes).forEach(([k, v]) => script.setAttribute(k, v));
  document.head.appendChild(script);
};

const injectInlineScript = (id, code) => {
  if (document.getElementById(id)) return;
  const script = document.createElement('script');
  script.id = id;
  script.textContent = code;
  document.head.appendChild(script);
};

const removeScript = id => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

// ─── Load ─────────────────────────────────────────────────────────────────────

export const loadAnalytics = () => {
  const gaId = process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
  const plausibleDomains = process.env.REACT_APP_PLAUSIBLE_DOMAINS;
  const posthogKey = process.env.REACT_APP_POSTHOG_API_KEY;
  const posthogHost = process.env.REACT_APP_POSTHOG_HOST || 'https://app.posthog.com';

  // Google Analytics 4
  if (gaId) {
    injectScript(SCRIPT_IDS.GA, `https://www.googletagmanager.com/gtag/js?id=${gaId}`);
    injectInlineScript(
      `${SCRIPT_IDS.GA}-init`,
      `window.dataLayer = window.dataLayer || [];
       function gtag(){dataLayer.push(arguments);}
       gtag('js', new Date());
       gtag('config', '${gaId}');`
    );
  }

  // Plausible Analytics (privacy-friendly, no cookies)
  if (plausibleDomains) {
    injectScript(SCRIPT_IDS.PLAUSIBLE, 'https://plausible.io/js/plausible.js', {
      'data-domain': plausibleDomains,
      defer: '',
    });
  }

  // PostHog
  if (posthogKey) {
    injectInlineScript(
      SCRIPT_IDS.POSTHOG,
      `!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]);t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+" (stub)"},o="capture identify alias people.set people.set_once set register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
      posthog.init('${posthogKey}', {api_host: '${posthogHost}'});`
    );
  }
};

// ─── Remove ───────────────────────────────────────────────────────────────────

export const removeAnalytics = () => {
  // Remove injected scripts
  Object.values(SCRIPT_IDS).forEach(id => {
    removeScript(id);
    removeScript(`${id}-init`);
  });

  // Clear GA cookies
  document.cookie.split(';').forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    if (/^(_ga|_gid|_gat|_gcl)/.test(name)) {
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${window.location.hostname}`;
    }
  });

  // Reset PostHog if loaded
  if (window.posthog?.opt_out_capturing) {
    window.posthog.opt_out_capturing();
  }
};

// ─── Check consent ─────────────────────────────────────────────────────────────

export const hasAnalyticsConsent = () => {
  try {
    // vanilla-cookieconsent stores consent in cookie 'cc_cookie'
    const match = document.cookie.match(/cc_cookie=([^;]+)/);
    if (!match) return false;
    const data = JSON.parse(decodeURIComponent(match[1]));
    return Array.isArray(data.categories) && data.categories.includes('analytics');
  } catch (_) {
    return false;
  }
};
